import { ChangeDetectorRef, Component, signal, OnInit, WritableSignal } from '@angular/core'
import { FormGroup } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { FieldType, FieldTypeConfig, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core'
import { DateTime } from 'luxon'
import { ApplicationService } from 'src/app/services/application.service'
import { BillingService } from 'src/app/services/billing.service'
import { OrderService } from 'src/app/services/order.service'
import { ProfileService } from 'src/app/services/profile.service'
import * as _ from 'lodash'
import { buildField } from 'src/app/services/fields.service'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { MatButtonModule } from '@angular/material/button'

@Component({
	selector: 'gwc-stepper',
	templateUrl: './stepper.component.html',
	styleUrls: ['./stepper.component.scss'],
	imports: [
		FormlyModule,
		MatProgressBarModule,
		MatButtonModule
	]
})

export class StepperComponent extends FieldType<FieldTypeConfig> implements OnInit {
	public current_id: WritableSignal<number> = signal(0)
	public current_step: WritableSignal<number> = signal(1)
	public stepForm: WritableSignal<FormlyFieldConfig> = signal({} as FormlyFieldConfig)
	public steps: WritableSignal<number> = signal(0)
	public progress: WritableSignal<number> = signal(0)
	public form_obj: FormGroup = new FormGroup({})
	public stepForms: FormGroup[] = []
	private params!: { [key: string]: string }
	public model_copy: WritableSignal<{ [key: string]: string }> = signal({})
	public saving: WritableSignal<boolean> = signal(false)

	constructor(
		private activatedRoute: ActivatedRoute,
		private orderService: OrderService,
		private profileService: ProfileService,
		private applicationService: ApplicationService,
		private router: Router,
		private changeDetectorRef: ChangeDetectorRef,
		public billingService: BillingService
	) {
		super()
		this.activatedRoute.pathFromRoot.forEach((route) => {
			this.params = { ...this.params, ...route.snapshot.params }
		})
	}

	ngOnInit() {
		this.getStep()
		this.resetModelCopy()

		this.applicationService.calculateProgress(this.props['uuid'])
			.subscribe({
				next: response => {
					this.progress.set(response.progress)
				},
				error: error => {
					console.error(error)
				}
			})
	}

	ngAfterViewInit() {
		this.progress.set(this.props['progress'] || 0)
		this.countSteps()
	}

	private resetModelCopy(): void {
		this.model_copy = _.cloneDeep(this.model)
	}

	private getStep() {
		if (this.props['list'] !== undefined) {
			const new_step = buildField({ ...this.props['list'][this.current_id()] })
			this.stepForm.set(new_step)
		}
	}

	public stepChange(direction: string): void {
		if (this.form_obj.valid || direction === 'back') {
			if (direction === 'continue'&& this.props['product_type'] === 'passport') {
				const desired = this.applicationService.passportCheck(this.model, this.model.application.addons.includes('passport_card'), this.model.application.product.subtype)
				
				this.model.application.product.desired = desired
				this.model.application.product.adjudicated = !['passport-renewal', 'second-passport', 'name-change'].includes(desired)
			}
			const step = direction === 'back' ? -1 : 1
			let new_step = this.current_id() + step
			let field: FormlyFieldConfig | undefined = this.field.props['list']?.[new_step]

			while (this.hideStep(field)) {
				new_step += step
				if (new_step > this.field.props['list']?.length) {
					this.finishForm()
					break
				}

				field = this.field.props['list']?.[new_step]
			}

			this.current_id.set(new_step)
			this.getStep()
			this.current_step.update(value => value + step)
			window.scrollTo(0, 0)
			this.form_obj = new FormGroup({})
			this.orderService.nextPage.next(this.props['uuid'])
			if (!_.isEqual(this.model, this.model_copy)) {
				this.saveModel()
				this.countSteps()
			}
		} else {
			this.form_obj.markAllAsTouched()
		}
	}

	private hideStep(field: FormlyFieldConfig | undefined): boolean {
		try {
			if (!field) return true

			// Needs to be here for eval
			let model = this.model
			let expression: string = field.props?.['hide'] as string
			if (!expression) return false

			let dt = DateTime

			return eval(expression)
		} catch {
			return false
		}
	}

	private countSteps(finish: boolean = false, model_changed: boolean = false) {
		let count = 0

		this.props['list'].forEach((field: any, index: number) => {
			if (!this.hideStep(field)) {
				count += 1
			}
		})

		this.steps.set(count)

		if (finish) {
			this.router.navigate(['../../'], { relativeTo: this.activatedRoute })
		}
	}

	public finishForm(): void {
		this.saving.set(true)
		const current_form = this.form_obj
		if (current_form.valid || this.billingService.paymentCompleted()) {
			this.applicationService.saveProgress(this.props['uuid'], 100)
				.subscribe(response => {
					this.closeForm(false)
					this.saving.set(false)
				})
		} else {
			current_form.markAllAsTouched()
		}
	}

	public closeForm(calculate_progress: boolean = true) {
		if (!_.isEqual(this.model, this.model_copy)) {
			this.saveModel()
			this.orderService.submittedSubject.next(this.props['uuid'])

			if (calculate_progress) {
				this.countSteps(true, true)
			}
		}

		this.router.navigate(['../../'], { relativeTo: this.activatedRoute })
	}

	private saveModel(): void {
		this.profileService.saveProfileWithApplication(this.props['uuid'], this.model)
			.subscribe({
				next: response => {
					this.resetModelCopy()
					this.applicationService.calculateProgress(this.props['uuid'])
						.subscribe({
							next: response => {
								this.progress.set(response.progress)
							},
							error: error => {
								console.error(error)
							}
						})
				},
				error: error => {
					console.error(error)
				}
			})
	}

	public getOptions(id: number) {
		let options = this.options
		options.formState.active = id === this.current_id()
		return options
	}
}
